import React, { useEffect, useState } from 'react';
import { LoaderCircle } from 'lucide-react';
import { DEFAULT_LLM_MODEL_KEY } from '../../constants/llmModels';
import { useI18n } from '../../hooks/useI18n';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { AiReferenceImagesField } from '../../components/shared/AiReferenceImagesField';
import { ModalComposerField } from '../../components/shared/ModalComposerField';

const emptyForm = {
  title: '',
  context: '',
  model: DEFAULT_LLM_MODEL_KEY
};

export const CreateTaskModal = ({ open, onClose, onCreate, loading = false, loadingLabel = '' }) => {
  const { t } = useI18n();
  const [form, setForm] = useState(emptyForm);
  const [referenceImages, setReferenceImages] = useState([]);
  const [titleError, setTitleError] = useState('');

  useEffect(() => {
    if (!open) {
      setTitleError('');
      return;
    }

    setForm(emptyForm);
    setReferenceImages([]);
  }, [open]);

  const handleClose = () => {
    if (loading) {
      return;
    }

    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const title = form.title.trim();
    const context = form.context.trim();
    let hasError = false;

    if (!title) {
      setTitleError(t('chatTitleRequired'));
      hasError = true;
    } else {
      setTitleError('');
    }

    if (hasError || loading) {
      return;
    }

    await onCreate({
      title,
      context,
      model: form.model,
      referenceImages
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeDisabled={loading}
      title={t('newChat')}
      panelClassName="max-w-lg rounded-[1.75rem] p-4 md:p-5"
      contentClassName="mb-2.5"
      titleClassName="text-base font-bold md:text-lg"
    >
      <form className="space-y-3" onSubmit={handleSubmit}>
        <div className="space-y-1">
          <Input
            label={t('taskTitleLabel')}
            value={form.title}
            required
            disabled={loading}
            maxLength={120}
            placeholder={t('chatTitlePlaceholder')}
            className="py-2.5"
            onChange={(event) => {
              setForm({ ...form, title: event.target.value });
              if (titleError) {
                setTitleError('');
              }
            }}
          />
          {titleError ? (
            <p className="text-xs text-rose-600 dark:text-rose-300" role="alert">
              {titleError}
            </p>
          ) : null}
        </div>

        <ModalComposerField
          id="task-context"
          label={t('taskDescriptionLabel')}
          value={form.context}
          disabled={loading}
          placeholder={t('taskDescriptionPlaceholder')}
          showModelSelect={false}
          showIncludesImageBadge={referenceImages.length > 0}
          onChange={(context) => setForm({ ...form, context })}
        />

        <AiReferenceImagesField
          value={referenceImages}
          disabled={loading}
          onChange={setReferenceImages}
        />

        <div className="flex flex-col-reverse gap-2 border-t border-border/70 pt-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? (
              <>
                <LoaderCircle size={16} className="animate-spin" />
                {loadingLabel || t('creatingTask')}
              </>
            ) : (
              t('create')
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
